import { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      const quotes = JSON.parse(localStorage.getItem('quotes') || '[]');
      quotes.push({ ...formData, id: Date.now(), date: new Date().toISOString(), status: 'pending' });
      localStorage.setItem('quotes', JSON.stringify(quotes));
      toast.success('Quote request submitted!', { description: 'We will get back to you within 24 hours.' });
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const contactInfo = [
    { icon: Phone, title: 'Phone', details: '1-800-DUMPSTER', link: 'tel:1-800-DUMPSTER' },
    { icon: Mail, title: 'Email', details: 'info@dumpsterrental.com', link: 'mailto:info@dumpsterrental.com' },
    { icon: MapPin, title: 'Address', details: '123 Waste Management Blvd, Suite 100', link: '#' },
    { icon: Clock, title: 'Hours', details: 'Mon-Fri: 7am-7pm, Sat: 8am-5pm', link: '#' },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-16 md:pb-24 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">Get Your Free Quote</h1>
            <p className="text-lg text-muted-foreground leading-relaxed">Tell us about your project and we will provide a customized quote within 24 hours.</p>
          </div>
        </div>
      </section>
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="border-border hover:shadow-lg transition-smooth group">
                  <CardContent className="pt-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3 mx-auto group-hover:bg-gradient-primary transition-smooth">
                      <Icon className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-smooth" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">{info.title}</h3>
                    {info.link !== '#' ? (
                      <a href={info.link} className="text-sm text-muted-foreground hover:text-primary transition-smooth">{info.details}</a>
                    ) : (
                      <p className="text-sm text-muted-foreground">{info.details}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-6 text-center">Request a Quote</h2>
            <Card className="border-border shadow-lg">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" required className="border-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@example.com" required className="border-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" required className="border-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service">Service Type *</Label>
                    <Select value={formData.service} onValueChange={(value) => setFormData({...formData, service: value})} required>
                      <SelectTrigger className="border-input">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential Dumpster Rental</SelectItem>
                        <SelectItem value="commercial">Commercial Waste Services</SelectItem>
                        <SelectItem value="construction">Construction Debris Removal</SelectItem>
                        <SelectItem value="recycling">Recycling Services</SelectItem>
                        <SelectItem value="junk-removal">Junk Removal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Project Details</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Tell us about your project..." rows={4} className="border-input resize-none" />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-primary hover:shadow-glow transition-smooth text-base" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? <span className="flex items-center justify-center"><span className="animate-spin mr-2">⏳</span>Submitting...</span> : <span className="flex items-center justify-center"><Send className="w-4 h-4 mr-2" />Submit Request</span>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
